import React, { useCallback, useEffect, useState } from "react";
import { Typography } from "@mui/material";
import RunDetailsTable from "./RunDetailsTable/RunDetailsTable";
import classes from "./LandedCostResults.module.css";
import { getRunDetailsBatchJobs } from "../../API/runDetailsBatchJobApi";
import { getRunDetailsHistory } from "../../API/runDetailsHistoryApi";

const VIEW = {
  RUN_DATA: "run-data",
  ITEM_WORKFLOW: "item-workflow",
};

const NewItemView = () => {
  const [activeView, setActiveView] = useState(VIEW.RUN_DATA);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [runSummaries, setRunSummaries] = useState([]);
  const [runSummariesLoading, setRunSummariesLoading] = useState(false);
  const [runDetails, setRunDetails] = useState([]);
  const [runDetailsLoading, setRunDetailsLoading] = useState(false);
  const [runDataSortModel, setRunDataSortModel] = useState([]);
  const [runDataFilterModel, setRunDataFilterModel] = useState({ items: [] });
  const [runDataPage, setRunDataPage] = useState(0);
  const [itemWorkflowPage, setItemWorkflowPage] = useState(0);

  const handleViewChange = (view, batchId = null) => {
    setActiveView(view);
    setSelectedBatchId(view === VIEW.ITEM_WORKFLOW ? batchId : null);
  };

  useEffect(() => {
    if (activeView !== VIEW.ITEM_WORKFLOW) {
      return undefined;
    }
    const handleBackspace = (event) => {
      const target = event?.target;
      const tagName = (target?.tagName || "").toUpperCase();
      const isEditable =
        tagName === "INPUT" || tagName === "TEXTAREA" || target?.isContentEditable;

      if (event.key === "Backspace" && !isEditable) {
        event.preventDefault();
        setActiveView(VIEW.RUN_DATA);
        setSelectedBatchId(null);
      }
    };

    window.addEventListener("keydown", handleBackspace);
    return () => window.removeEventListener("keydown", handleBackspace);
  }, [activeView]);

  useEffect(() => {
    const fetchRunSummaries = async () => {
      setRunSummariesLoading(true);
      try {
        const data = await getRunDetailsBatchJobs();
        const normalizedData = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.content)
          ? data.content
          : [];
        setRunSummaries(normalizedData);
      } finally {
        setRunSummariesLoading(false);
      }
    };

    fetchRunSummaries();
  }, []);

  const fetchRunDetails = useCallback(async () => {
    if (!selectedBatchId) {
      setRunDetails([]);
      return;
    }

    setRunDetailsLoading(true);
    try {
      const data = await getRunDetailsHistory(selectedBatchId);
      const normalizedData = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.content)
        ? data.content
        : [];
      setRunDetails(normalizedData);
    } finally {
      setRunDetailsLoading(false);
    }
  }, [selectedBatchId]);

  useEffect(() => {
    fetchRunDetails();
  }, [fetchRunDetails]);

  const renderRunData = () => (
    <section className={classes.section}>
      <header className={classes.sectionHeader}>
        <Typography variant="h6" component="h2" className={classes.sectionTitle}>
          Run Data
        </Typography>
      </header>
      <RunDetailsTable
        runDetails={runSummaries}
        loading={runSummariesLoading}
        variant="summary"
        sortModel={runDataSortModel}
        onSortModelChange={setRunDataSortModel}
        filterModel={runDataFilterModel}
        onFilterModelChange={setRunDataFilterModel}
        page={runDataPage}
        onPageChange={setRunDataPage}
        onBatchNavigate={(batchId) => {
          handleViewChange(VIEW.ITEM_WORKFLOW, batchId);
        }}
      />
    </section>
  );

  const renderItemWorkflow = () => (
    <section className={classes.section}>
      <header className={classes.sectionHeader}>
        <Typography variant="h6" component="h2" className={classes.sectionTitle}>
          Item Workflow{selectedBatchId ? ` for Batch ${selectedBatchId}` : ""}
        </Typography>
      </header>
      <RunDetailsTable
        runDetails={runDetails}
        loading={runDetailsLoading}
        variant="detail"
        showActions={true}
        selectedBatchId={selectedBatchId}
        onRefreshData={fetchRunDetails}
        page={itemWorkflowPage}
        onPageChange={setItemWorkflowPage}
        onBatchNavigate={(batchId) => setSelectedBatchId(batchId)}
      />
    </section>
  );

  return (
    <section className={classes.page}>
      <header className={classes.header}>
        <div>
          <h1 className={classes.title}>New Item Flow</h1>
          <p className={classes.subtitle}>
            Review the latest run data alongside item workflow details in a single workspace.
          </p>
        </div>
      </header>

      <div className={classes.content}>
        {activeView === VIEW.RUN_DATA ? renderRunData() : renderItemWorkflow()}
      </div>
    </section>
  );
};

export default NewItemView;
